import { useState, useEffect, useMemo } from "react";
import { Search, ExternalLink, ArrowUpDown, Loader2 } from "lucide-react";
import { API, axios } from "@/api";

function RateBadge({ rate }) {
  const pct = Math.round((rate || 0) * 100);
  const color = pct >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                pct >= 70 ? "bg-amber-50 text-amber-700 border-amber-200" :
                "bg-red-50 text-red-700 border-red-200";
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${color}`}>{pct}%</span>;
}

export default function PlaybooksPanel() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("success_rate");
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/playbooks`)
      .then(res => setPlaybooks(res.data?.playbooks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) { setSortAsc(!sortAsc); }
    else { setSortKey(key); setSortAsc(false); }
  };

  const filtered = useMemo(() => {
    let list = playbooks;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.brand_name || "").toLowerCase().includes(q) || (p.brand_slug || "").toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? av - bv : bv - av;
    });
    return list;
  }, [playbooks, search, sortKey, sortAsc]);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const SortHeader = ({ label, field, className = "" }) => (
    <button onClick={() => handleSort(field)} className={`flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition ${className}`}>
      {label} <ArrowUpDown className={`w-2.5 h-2.5 ${sortKey === field ? "text-sky-500" : ""}`} />
    </button>
  );

  return (
    <div className="space-y-3" data-testid="playbooks-panel">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brands..."
          className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-sky-200"
          data-testid="playbooks-search"
        />
      </div>

      {/* Stats */}
      <div className="flex gap-3 text-[10px]">
        <span className="text-slate-400">{filtered.length} playbooks</span>
        <span className="text-emerald-600 font-bold">{filtered.filter(p => (p.success_rate || 0) >= 0.9).length} high-success</span>
        <span className="text-red-500 font-bold">{filtered.filter(p => (p.success_rate || 0) < 0.7).length} needs work</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs" data-testid="playbooks-table">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left pb-2 pr-2"><SortHeader label="Brand" field="brand_name" /></th>
              <th className="text-left pb-2 pr-2"><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Best Source</span></th>
              <th className="text-center pb-2 pr-2"><SortHeader label="Success" field="success_rate" className="justify-center" /></th>
              <th className="text-center pb-2 pr-2"><SortHeader label="Avg Score" field="avg_completeness_score" className="justify-center" /></th>
              <th className="text-right pb-2"><SortHeader label="Last Verified" field="last_verified" className="justify-end" /></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.brand_slug || i} className="border-b border-slate-50 hover:bg-slate-50/50 transition" data-testid={`playbook-row-${i}`}>
                <td className="py-2 pr-2">
                  <p className="font-semibold text-slate-800 truncate max-w-[160px]">{p.brand_name}</p>
                </td>
                <td className="py-2 pr-2">
                  {p.best_source_url ? (
                    <a href={p.best_source_url} target="_blank" rel="noopener noreferrer"
                       className="text-sky-500 hover:text-sky-600 flex items-center gap-0.5 truncate max-w-[180px]">
                      {new URL(p.best_source_url).hostname.replace("www.", "")} <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                    </a>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2 pr-2 text-center"><RateBadge rate={p.success_rate} /></td>
                <td className="py-2 pr-2 text-center">
                  <span className="font-semibold text-slate-700">{Math.round(p.avg_completeness_score || 0)}</span>
                  <span className="text-slate-300">/100</span>
                </td>
                <td className="py-2 text-right text-slate-400">
                  {p.last_verified ? new Date(p.last_verified).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-xs text-slate-400 py-4">No playbooks match your search</p>
      )}
    </div>
  );
}
